import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/Card/Card";
import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
} from "recharts";
import { useTheme } from "@/context/ThemeContext";
import { useState, useEffect } from "react";
import { ChartRangeSlider } from "./ChartRangeSlider";
import { MultiSelect } from "@/components/MultiSelect/MultiSelect";
import { formatCurrency } from "@/modules/stochastic/features/Reports/SCDashboard/utils/chart";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Info } from "lucide-react";

export interface TotalHouseholdsByZipCodeDataItem {
  existingCustomer: number;
  newCustomer: number;
  total: number;
  zipCode: string;
}

export interface TotalHouseholdsByZipCodeData {
  items: TotalHouseholdsByZipCodeDataItem[];
  avgNewCustomerHouseholdValue: number;
  totalNewCustomerHouseholds: number;
  totalZipCodes: number;
  availableZipCodes: string[];
}

type ChartProps = {
  chartId?: string;
  initialData: TotalHouseholdsByZipCodeData;
};

const formatNumber = (value: number) => {
  return new Intl.NumberFormat().format(value);
};

export default function TotalHouseholdsByZipCodeChart({
  initialData,
}: ChartProps) {
  const { theme } = useTheme();

  if (initialData === undefined) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="text-fontColor dark:text-light">
            Total Households by ZIP Code
          </CardTitle>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Household distribution across your service area
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-64 text-gray-500">
            Preparing data...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!initialData || !initialData.items || initialData.items.length === 0) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="text-fontColor dark:text-light">
            Total Households by ZIP Code
          </CardTitle>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Household distribution across your service area
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-64 text-gray-500">
            No data available
          </div>
        </CardContent>
      </Card>
    );
  }

  const maxTotalValue = Math.max(
    ...initialData.items.map((item) => item.total),
  );
  const minTotalValue = Math.min(
    ...initialData.items.map((item) => item.total),
  );

  const [yAxisRange, setYAxisRange] = useState<[number, number]>([
    minTotalValue,
    maxTotalValue,
  ]);
  const [barRange, setBarRange] = useState<[number, number]>([
    1,
    initialData.items.length,
  ]);
  const [selectedZipCodes, setSelectedZipCodes] = useState<string[]>([]);

  const filteredByZipCode =
    selectedZipCodes.length > 0
      ? initialData.items.filter((item) => {
          return selectedZipCodes.includes(item.zipCode);
        })
      : initialData.items;

  const filteredByYAxis = filteredByZipCode.filter(
    (item) => item.total >= yAxisRange[0] && item.total <= yAxisRange[1],
  );

  useEffect(() => {
    setBarRange([1, filteredByYAxis.length || 1]);
  }, [yAxisRange, selectedZipCodes]);

  const handleYAxisChange = (value: number | [number, number]) => {
    if (Array.isArray(value)) {
      setYAxisRange(value);
    }
  };

  const handleBarRangeChange = (value: number | [number, number]) => {
    if (Array.isArray(value)) {
      setBarRange(value);
    }
  };

  const zipCodeOptions = (initialData.availableZipCodes || []).map(
    (zipCode) => ({
      label: String(zipCode),
      value: String(zipCode),
    }),
  );

  const effectiveEnd = Math.min(barRange[1], filteredByYAxis.length);
  const displayData = filteredByYAxis.slice(barRange[0] - 1, effectiveEnd);

  console.log("Debug info:", {
    selectedZipCodes,
    availableZipCodes: initialData.availableZipCodes,
    actualZipCodesInData: Array.from(
      new Set(initialData.items.map((item) => item.zipCode)),
    ).slice(0, 5),
    filteredByZipCode: filteredByZipCode.length,
    filteredByYAxis: filteredByYAxis.length,
    displayData: displayData.length,
    barRange,
    yAxisRange,
    sampleFilteredItem: filteredByZipCode[0],
  });

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center gap-2">
          <CardTitle className="text-fontColor dark:text-light">
            Total Households by ZIP Code
          </CardTitle>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Info className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 shadow-lg">
                <p>
                  Identifies where market capture is strongest and where
                  opportunity remains.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Household distribution across your service area
        </p>
      </CardHeader>
      <CardContent>
        <div className="mb-6">
          <MultiSelect
            className="w-full [&_button]:!bg-gray-50 dark:[&_button]:!bg-gray-800 [&_button]:!border-gray-200 dark:[&_button]:!border-white/20"
            onChange={(value) => {
              setSelectedZipCodes(value);
            }}
            options={zipCodeOptions}
            placeholder="Select ZIP codes to filter..."
            value={selectedZipCodes}
          />
        </div>

        <div className="flex">
          <div className="flex items-stretch justify-start mr-4">
            <ChartRangeSlider
              className="h-full"
              max={maxTotalValue}
              min={minTotalValue}
              onChange={handleYAxisChange}
              orientation="vertical"
              value={yAxisRange}
            />
          </div>

          <div className="flex-1">
            <ResponsiveContainer height={500} width="100%">
              <BarChart
                data={displayData}
                margin={{
                  bottom: 5,
                  left: 20,
                  right: 30,
                  top: 20,
                }}
              >
                <CartesianGrid
                  stroke={theme === "dark" ? "#374151" : "#e5e7eb"}
                  strokeDasharray="3 3"
                />
                <XAxis
                  angle={-90}
                  dataKey="zipCode"
                  fontSize={14}
                  height={100}
                  stroke={theme === "dark" ? "#9CA3AF" : "#4B5563"}
                  textAnchor="end"
                />
                <YAxis
                  domain={yAxisRange}
                  stroke={theme === "dark" ? "#9CA3AF" : "#4B5563"}
                  tickFormatter={formatNumber}
                />
                <ChartTooltip
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded p-2">
                          <div className="font-semibold text-blue-600">
                            {label}
                          </div>
                          <div style={{ color: "#F59E0B" }}>
                            Existing Customer:{" "}
                            {formatNumber(data.existingCustomer)}
                          </div>
                          <div style={{ color: "#EF4444" }}>
                            New Customer: {formatNumber(data.newCustomer)}
                          </div>
                          <div
                            style={{
                              borderTop: "1px solid #ccc",
                              paddingTop: "4px",
                              marginTop: "4px",
                            }}
                          >
                            Total Households: {formatNumber(data.total)}
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                  contentStyle={{
                    backgroundColor: theme === "dark" ? "#1F2937" : "#FFFFFF",
                    borderColor: theme === "dark" ? "#374151" : "#E5E7EB",
                    color: theme === "dark" ? "#F3F4F6" : "#1F2937",
                  }}
                />
                <Legend wrapperStyle={{ paddingTop: "20px" }} />
                <Bar
                  dataKey="newCustomer"
                  fill="#EF4444"
                  name="New Customer"
                  stackId="household"
                />
                <Bar
                  dataKey="existingCustomer"
                  fill="#F59E0B"
                  name="Existing Customer"
                  radius={[4, 4, 0, 0]}
                  stackId="household"
                />
              </BarChart>
            </ResponsiveContainer>

            <div className="mt-8 px-2">
              <ChartRangeSlider
                max={filteredByYAxis.length}
                min={1}
                onChange={handleBarRangeChange}
                orientation="horizontal"
                value={[
                  barRange[0],
                  Math.min(barRange[1], filteredByYAxis.length),
                ]}
              />
            </div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-white/20 rounded-md p-4 shadow-sm text-center">
            <div className="text-gray-500 dark:text-gray-300 text-sm">
              Total New Customer Households
            </div>
            <div className="font-bold text-lg dark:text-white">
              {formatNumber(initialData.totalNewCustomerHouseholds)}
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-white/20 rounded-md p-4 shadow-sm text-center">
            <div className="text-gray-500 dark:text-gray-300 text-sm">
              Avg New Customer Household Value
            </div>
            <div className="font-bold text-lg dark:text-white">
              {formatCurrency(initialData.avgNewCustomerHouseholdValue)}
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-white/20 rounded-md p-4 shadow-sm text-center">
            <div className="text-gray-500 dark:text-gray-300 text-sm">
              Total ZIP Codes
            </div>
            <div className="font-bold text-lg dark:text-white">
              {formatNumber(initialData.totalZipCodes)}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
